import { createAction } from "redux-actions";
import {
  SALE,
  BUNDLED_SALES,
  START,
  FETCH_ITEMS,
  FETCH_ITEM,
  CREATE_ITEM,
  RESET,
  FS_CREATE_ITEMS,
  FS_DELETE_ITEMS,
  FS_UPDATE_ITEMS,
} from "../common";
import {
  updateSaleApi,
  updateBundledSalesApi,
  deleteBundledSalesApi,
  deleteManySales,
  deleteReturn,
  moveSaleApi,
  moveBundledSalesApi,
  addSaleApi,
  reviewManySales,
} from "src/apiService/modules/sales";
import { addInventory } from "src/apiService/modules/inventories";
import type { AppState } from "src/store";
import type { AppDispatch } from "src/store/types";
import type { Sale } from "src/interfaces/sale.interface";
import type { Inventory } from "src/interfaces/inventory.interface";
import { calculateGrossProfit, calculateNetProfit } from "src/utils/sales";
import { withOptimisticUpdate } from "src/store/utils/withOptimisticUpdate";
import { getSaleById } from "src/store/sale/selector";
import { userIncomeTaxRateSelector } from "src/store/system/selector";

export const SET_PENDING_UPLOADS = "SET_PENDING_UPLOADS";
export const UPDATE_PENDING_UPLOAD = "UPDATE_PENDING_UPLOAD";
export const SHIFT_PENDING_UPLOAD = "SHIFT_PENDING_UPLOAD";
export const SET_UPLOAD_FILENAME = "SET_UPLOAD_FILENAME";
export const LOAD_SYNC_SALES = "LOAD_SYNC_SALES";
export const SET_UPLOADS_FROM_EBAY = "SET_UPLOADS_FROM_EBAY";
export const SET_EBAY_EXTRA_ROWS = "SET_EBAY_EXTRA_ROWS";
export const SET_OLDEST_SALE_YEAR = "SET_OLDEST_SALE_YEAR";
export const PATCH_SALES_RECORDS = "PATCH_SALES_RECORDS";

export const fetchItems = createAction(SALE + FETCH_ITEMS + START);

export const fetchItem = createAction(SALE + FETCH_ITEM + START);
export const createItem = createAction(SALE + CREATE_ITEM + START);
export const addBundledSales = createAction(
  BUNDLED_SALES + CREATE_ITEM + START
);
export const setPendingUploads = createAction(SET_PENDING_UPLOADS);
export const updatePendingUpload = createAction(UPDATE_PENDING_UPLOAD);
export const setUploadFilename = createAction(SET_UPLOAD_FILENAME);
export const shiftPendingUpload = createAction(SHIFT_PENDING_UPLOAD);
export const loadNewSyncSales = createAction(LOAD_SYNC_SALES + START);
export const setUploadsFromEbay = createAction(SET_UPLOADS_FROM_EBAY);
export const setEbayExtraRows = createAction(SET_EBAY_EXTRA_ROWS);
export const setOldestSaleYear = createAction<number | null>(
  SET_OLDEST_SALE_YEAR
);

export const reset = createAction(SALE + RESET);

export const addSalesRecords = createAction(SALE + FS_CREATE_ITEMS);
export const updateSalesRecords = createAction(SALE + FS_UPDATE_ITEMS);
export const deleteSalesRecords = createAction(SALE + FS_DELETE_ITEMS);

/**
 * Merge-only update for a few sale fields. Unlike `updateSalesRecords`, this
 * never replaces or re-maps the whole document, so unrelated fields changed by
 * another listener/action are preserved.
 */
export const patchSalesRecords =
  createAction<Array<Partial<Sale> & { id: Sale["id"] }>>(PATCH_SALES_RECORDS);

function buildOptimisticSale(
  previous: Sale | undefined,
  payload: Partial<Sale> & { id: string },
  incomeTaxRate: number
): Sale {
  const merged = {
    ...(previous || {}),
    ...payload,
    id: payload.id,
  } as Sale;
  const gross_profit = calculateGrossProfit(merged);
  const estimated_income_tax = gross_profit * incomeTaxRate;
  return {
    ...merged,
    gross_profit,
    estimated_income_tax,
    net_profit: calculateNetProfit({
      gross_profit,
      estimated_income_tax,
      other_business_costs: merged.other_business_costs,
    }),
  };
}

function normalizeSaleIds(
  payload: Array<string | { id: string }>
): Sale["id"][] {
  return payload
    .map((item) => (typeof item === "string" ? item : item?.id))
    .filter((id): id is Sale["id"] => !!id);
}

/**
 * Optimistically merges the sale into Redux, writes Firestore, rolls back on error.
 * Callers own success/error toasts.
 */
export function updateItem(payload: Partial<Sale> & { id: string }) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!payload?.id) return;

    const state = getState();
    const previous = getSaleById(state, payload.id);
    const next = buildOptimisticSale(
      previous,
      payload,
      userIncomeTaxRateSelector(state) || 0
    );

    await withOptimisticUpdate({
      apply: () => {
        dispatch(updateSalesRecords([next]));
      },
      revert: () => {
        if (previous) dispatch(updateSalesRecords([previous]));
        else dispatch(deleteSalesRecords([{ id: payload.id }]));
      },
      commit: () => updateSaleApi(payload),
    });
  };
}

/**
 * Optimistically flips the review flag (and optional vendors) in Redux, then
 * writes the batch. Used by the review dialogs so out-of-window rows disappear
 * without waiting for an all-time listener snapshot.
 */
export function reviewManySalesOptimistic(
  ids: Sale["id"][],
  unreviewed = false,
  vendors: Record<string, string> = {}
) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!ids?.length) return;

    const state = getState();
    const previousById = new Map<string, Sale>();
    const patches: Array<Partial<Sale> & { id: Sale["id"] }> = [];

    for (const id of ids) {
      const previous = getSaleById(state, id);
      if (!previous) continue;
      previousById.set(id, previous);
      const vendor = vendors[id];
      patches.push({
        id,
        unreviewed,
        ...(vendor ? { vendor } : {}),
      });
    }

    const revertPatches = patches.map((patch) => {
      const previous = previousById.get(patch.id);
      return {
        id: patch.id,
        unreviewed: previous?.unreviewed,
        ...("vendor" in patch ? { vendor: previous?.vendor } : {}),
      };
    });

    await withOptimisticUpdate({
      apply: () => {
        if (patches.length) dispatch(patchSalesRecords(patches));
      },
      revert: () => {
        if (revertPatches.length) dispatch(patchSalesRecords(revertPatches));
      },
      commit: () => reviewManySales(ids, unreviewed, vendors),
    });
  };
}

export function updateBundledItems(payload: Sale[]) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!payload?.length) return;

    const state = getState();
    const incomeTaxRate = userIncomeTaxRateSelector(state) || 0;
    const previousById = new Map<string, Sale>();
    const nextSales: Sale[] = [];

    for (const sale of payload) {
      if (!sale?.id) continue;
      const previous = getSaleById(state, sale.id);
      if (previous) previousById.set(sale.id, previous);
      nextSales.push(buildOptimisticSale(previous, sale, incomeTaxRate));
    }

    await withOptimisticUpdate({
      apply: () => {
        if (nextSales.length) dispatch(updateSalesRecords(nextSales));
      },
      revert: () => {
        if (previousById.size)
          dispatch(updateSalesRecords([...previousById.values()]));
      },
      commit: () => updateBundledSalesApi(payload),
    });
  };
}

export function deleteSalesOptimistic(ids: Sale["id"][]) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!ids?.length) return;

    const state = getState();
    const previous = ids
      .map((id) => getSaleById(state, id))
      .filter((sale): sale is Sale => !!sale);

    await withOptimisticUpdate({
      apply: () => {
        dispatch(deleteSalesRecords(ids.map((id) => ({ id }))));
      },
      revert: () => {
        if (previous.length) dispatch(addSalesRecords(previous));
      },
      commit: () => deleteManySales(ids),
    });
  };
}

/** Refunds are sale docs; reuse the same optimistic delete path. */
export function deleteReturnOptimistic(returnId: string) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!returnId) return;

    const previous = getSaleById(getState(), returnId);

    await withOptimisticUpdate({
      apply: () => {
        dispatch(deleteSalesRecords([{ id: returnId }]));
      },
      revert: () => {
        if (previous) dispatch(addSalesRecords([previous]));
      },
      commit: () => deleteReturn(returnId),
    });
  };
}

/**
 * Optimistically removes bundled sales from Redux, then deletes via the API.
 * Accepts sale objects or bare ids (both call sites exist).
 */
export function deleteBundledItems(payload: Array<string | { id: string }>) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const ids = normalizeSaleIds(payload);
    if (!ids.length) return;

    const state = getState();
    const previous = ids
      .map((id) => getSaleById(state, id))
      .filter((sale): sale is Sale => !!sale);

    await withOptimisticUpdate({
      apply: () => {
        dispatch(deleteSalesRecords(ids.map((id) => ({ id }))));
      },
      revert: () => {
        if (previous.length) dispatch(addSalesRecords(previous));
      },
      commit: () => deleteBundledSalesApi(ids.map((id) => ({ id }))),
    });
  };
}

/** Move sale → inventory: drop the sale from Redux immediately (P&L), restore on error. */
export function moveItem(payload: Sale) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!payload?.id) return;

    const previous = getSaleById(getState(), payload.id);

    await withOptimisticUpdate({
      apply: () => {
        dispatch(deleteSalesRecords([{ id: payload.id }]));
      },
      revert: () => {
        if (previous) dispatch(addSalesRecords([previous]));
      },
      commit: () => moveSaleApi(payload),
    });
  };
}

export function moveBundledItems(payload: Sale[]) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!payload?.length) return;

    const ids = normalizeSaleIds(payload);
    if (!ids.length) return;

    const state = getState();
    const previous = ids
      .map((id) => getSaleById(state, id))
      .filter((sale): sale is Sale => !!sale);

    await withOptimisticUpdate({
      apply: () => {
        dispatch(deleteSalesRecords(ids.map((id) => ({ id }))));
      },
      revert: () => {
        if (previous.length) dispatch(addSalesRecords(previous));
      },
      commit: () => moveBundledSalesApi(payload),
    });
  };
}

interface RecordReturnPayload {
  returnRecord: Sale;
  sale: Sale;
  inventory?: Partial<Inventory> | null;
}

/**
 * Record a return: optimistically add the refund sale and link it on the original.
 * Server may assign a different return id — we reconcile after commit.
 */
export function recordReturn(payload: RecordReturnPayload) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const { returnRecord, sale, inventory } = payload;
    if (!sale?.id || !returnRecord?.id) return;

    const state = getState();
    const incomeTaxRate = userIncomeTaxRateSelector(state) || 0;
    const previousSale = getSaleById(state, sale.id);
    const clientReturnId = returnRecord.id;
    const optimisticReturn = buildOptimisticSale(
      undefined,
      returnRecord,
      incomeTaxRate
    );
    const optimisticOriginal = buildOptimisticSale(
      previousSale,
      { ...sale, return_id: clientReturnId },
      incomeTaxRate
    );

    await withOptimisticUpdate({
      apply: () => {
        dispatch(addSalesRecords([optimisticReturn]));
        dispatch(updateSalesRecords([optimisticOriginal]));
      },
      revert: () => {
        dispatch(deleteSalesRecords([{ id: clientReturnId }]));
        if (previousSale) dispatch(updateSalesRecords([previousSale]));
        else dispatch(deleteSalesRecords([{ id: sale.id }]));
      },
      commit: async () => {
        const data = await addSaleApi(returnRecord);
        const serverReturnId = data.id;
        await updateSaleApi({ ...sale, return_id: serverReturnId });
        if (inventory) {
          await addInventory({ ...inventory, return_id: serverReturnId });
        }
        if (serverReturnId && serverReturnId !== clientReturnId) {
          dispatch(deleteSalesRecords([{ id: clientReturnId }]));
          dispatch(
            addSalesRecords([
              buildOptimisticSale(
                undefined,
                { ...returnRecord, id: serverReturnId },
                incomeTaxRate
              ),
            ])
          );
          dispatch(
            updateSalesRecords([
              buildOptimisticSale(
                previousSale,
                { ...sale, return_id: serverReturnId },
                incomeTaxRate
              ),
            ])
          );
        }
        return data;
      },
    });
  };
}
